/**
 * DiagPanel — small floating widget that lets the user copy the diag
 * trace to clipboard with one click. Visible only when ?diag=1 is in
 * the URL. Free no-op in production navigation.
 *
 * Why this exists: Russ tried to capture the CM post-completion hang
 * trace via DevTools but couldn't find the console prompt under the
 * settings overlay. This widget bypasses DevTools entirely — just
 * click the button on the page, paste back to engineering chat.
 *
 * Position: fixed bottom-right corner, navy chip with the trace count
 * + a copy button. Tucked away enough to not interfere with normal
 * page interaction but always reachable.
 */

import { useEffect, useState } from "react";
import { isDiagEnabled, getDiagCount, copyDiagTrace } from "../lib/diag.js";

export default function DiagPanel() {
  const [count, setCount] = useState(0);
  const [copied, setCopied] = useState(false);

  // Refresh the count display every 1s so the user can see the trace
  // growing as polling fires. Cheap — just reads window.__cmDiag.length.
  useEffect(() => {
    if (!isDiagEnabled()) return;
    const tick = () => setCount(getDiagCount());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!isDiagEnabled()) return null;

  const onCopy = async () => {
    const ok = await copyDiagTrace();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      // eslint-disable-next-line no-alert
      alert("Copy failed — clipboard permission denied. Try DevTools console:\n\n  copy(localStorage.getItem('__cmDiag'))");
    }
  };

  return (
    <div
      data-component="DiagPanel"
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 9999,
        background: "var(--ppr-color-accent-navy)",
        color: "var(--ppr-color-text-inverse)",
        padding: "10px 14px",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <span style={{ fontWeight: 600 }}>
        DIAG · {count} {count === 1 ? "entry" : "entries"}
      </span>
      <button
        type="button"
        onClick={onCopy}
        data-testid="diag-copy-trace"
        style={{
          padding: "6px 12px",
          background: copied
            ? "var(--ppr-color-accent-coral)"
            : "var(--ppr-color-text-inverse)",
          color: copied
            ? "var(--ppr-color-text-inverse)"
            : "var(--ppr-color-accent-navy)",
          border: "none",
          borderRadius: 4,
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        {copied ? "Copied ✓" : "Copy trace"}
      </button>
    </div>
  );
}
